// ─────────────────────────────────────────────────────────────────────────────
// THE SEAM.
//
// This is the ONLY file in `modules/infoHub/` allowed to import host app code. Everything
// else in the module goes through here, or through the module's own hooks and utils. To lift
// the module into another project, rewrite this one file against that project's API layer —
// the rest of the folder moves unchanged.
//
// ESLint enforces this: `no-restricted-imports` bans `services/`, `config/`, `context/` and
// `constants/` everywhere under `src/modules/` except `api/`. See ../README.md.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  ContentMenuPage,
  ContentPage,
  HubItem,
  HubItemDetail,
  HubItemKind,
  NewsletterDetail,
  NewsletterSummary,
} from './types';

import { API_BASE_URL } from '../../../config/config';
import {
  ArticleClient,
  ContentItemPublicDTO,
  FileClient,
  NewsItemClient,
  NewsletterClient,
  NewsletterPublicDTO,
  PagesClient,
  PagesDTO,
  PageContentDTO,
} from '../../../services/apiClient';
import axiosInstance from '../../../services/axiosInstance';

// The page tree is small and drives a menu that must render in full, so it's fetched in
// one unpaged call rather than walked page by page.
const MENU_PAGE_SIZE = 99999;

const client = (): PagesClient => new PagesClient(API_BASE_URL, axiosInstance);
const newsletterClient = (): NewsletterClient => new NewsletterClient(API_BASE_URL, axiosInstance);
const articleClient = (): ArticleClient => new ArticleClient(API_BASE_URL, axiosInstance);
const newsItemClient = (): NewsItemClient => new NewsItemClient(API_BASE_URL, axiosInstance);
const fileClient = (): FileClient => new FileClient(API_BASE_URL, axiosInstance);

// The generated client throws an `ApiException` (with `.status`) on a non-200; a raw axios
// failure carries `.response.status`. Treat either 404 shape as "not found".
const isNotFound = (error: unknown): boolean => {
  const e = error as { status?: number; response?: { status?: number } };
  return e?.status === 404 || e?.response?.status === 404;
};

const toContentPage = (dto: PageContentDTO): ContentPage => ({
  id: dto.id ?? '',
  slug: dto.slug ?? '',
  slugFull: dto.slugFull ?? '',
  title: dto.title ?? '',
  description: dto.description ?? '',
  contentHtml: dto.contentHtml ?? '',
});

const toMenuPage = (dto: PagesDTO): ContentMenuPage => ({
  id: dto.id ?? '',
  parentPageId: dto.parentPageId ?? null,
  slugFull: dto.slugFull ?? '',
  title: dto.title ?? '',
  position: dto.position ?? null,
});

/**
 * Resolves a page by its full slug path (e.g. `about/committee`).
 *
 * One round trip: the backend does the DB lookup *and* reads the page HTML from blob
 * storage, returning both. The browser never touches storage (which would fail CORS) and
 * never receives the Unlayer design JSON, which is only needed for editing.
 *
 * Returns null when no page matches — the backend answers 404 for a missing page, and for
 * an inactive one unless `preview` is set. Any other failure throws.
 */
export const fetchPageBySlug = async (
  slug: string,
  preview?: boolean,
): Promise<ContentPage | null> => {
  try {
    const response = await client().getPageBySlug(slug, preview ?? undefined);
    const dto = response?.data;
    return dto ? toContentPage(dto) : null;
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
};

/** Fetches the pages that belong in the navigation: visible and active only. */
export const fetchMenuPages = async (): Promise<ContentMenuPage[]> => {
  const response = await client().searchPages({
    filters: { isVisible: true, isActive: true },
    page: 1,
    pageSize: MENU_PAGE_SIZE,
  });
  // nswag types `results` with an unrelated DTO's array type; the runtime shape is PagesDTO.
  const results = (response?.data?.results ?? []) as unknown as PagesDTO[];
  return results.map(toMenuPage);
};

// ─────────────────────────────────────────────────────────────────────────────
// Newsletters (public, active-only). Association-specific content on its own prefix.
// ─────────────────────────────────────────────────────────────────────────────

const toNewsletterSummary = (dto: NewsletterPublicDTO): NewsletterSummary => ({
  id: dto.id ?? '',
  slug: dto.slug ?? '',
  title: dto.title ?? '',
  year: dto.year ?? null,
  volume: dto.volume ?? '',
  date: dto.date ? new Date(dto.date as unknown as string).toISOString() : '',
  categoryTitle: dto.categoryTitle ?? '',
});

const toNewsletterDetail = (dto: NewsletterPublicDTO): NewsletterDetail => ({
  ...toNewsletterSummary(dto),
  contentHtml: dto.content ?? '',
});

export interface NewsletterArchiveQuery {
  year?: number;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface NewsletterArchivePage {
  items: NewsletterSummary[];
  total: number;
}

/** Fetches the active newsletter archive (newest first), without HTML bodies. */
export const fetchNewsletters = async (
  query: NewsletterArchiveQuery = {},
): Promise<NewsletterArchivePage> => {
  const response = await newsletterClient().getNewslettersPublic(
    query.year ?? undefined,
    query.search ?? undefined,
    query.page ?? undefined,
    query.pageSize ?? undefined,
  );
  const data = response?.data;
  const results = (data?.results ?? []) as unknown as NewsletterPublicDTO[];
  return {
    items: results.map(toNewsletterSummary),
    total: data?.totalCount ?? 0,
  };
};

/**
 * Resolves a single active newsletter (with its HTML body) by slug or GUID.
 * Returns null when nothing matches — the backend answers 404, which is an expected outcome
 * the reader should see a friendly message for, not a failure. Any other error throws.
 */
export const fetchNewsletter = async (idOrSlug: string): Promise<NewsletterDetail | null> => {
  try {
    const response = await newsletterClient().getNewsletterPublicByIdOrSlug(idOrSlug);
    const dto = response?.data;
    return dto ? toNewsletterDetail(dto) : null;
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Articles and news items. Two peers of the newsletter archive, read the same way: an
// active-only list without bodies, then a single item with its body.
//
// Both entities answer with the same `ContentItemPublicDTO` — they're split on lifecycle,
// not on shape — so one mapper covers both and `kind` is supplied by the caller, which is
// what decides the URL prefix and the wording.
// ─────────────────────────────────────────────────────────────────────────────

const toHubItem = (kind: HubItemKind, dto: ContentItemPublicDTO): HubItem => ({
  id: dto.id ?? '',
  kind,
  title: dto.title ?? '',
  slug: dto.slug ?? '',
  date: dto.date ? new Date(dto.date as unknown as string).toISOString() : '',
  summary: dto.summary ?? '',
  categoryId: dto.contentCategoryId ?? '',
  categoryTitle: dto.categoryTitle ?? '',
  pictureUrl: dto.pictureUrl ?? '',
  // The public reads never expose FileRecord ids — the backend resolves pictures to URLs.
  pictureFileRecordId: '',
  externalUrl: dto.externalUrl ?? '',
  // Only active items are returned at all, so anything that arrives here is live.
  isActive: true,
});

export interface HubArchiveQuery {
  categoryId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface HubArchivePage {
  items: HubItem[];
  total: number;
}

/** Fetches the active article library or news feed (newest first), without HTML bodies. */
export const fetchHubItems = async (
  kind: HubItemKind,
  query: HubArchiveQuery = {},
): Promise<HubArchivePage> => {
  const args = [
    query.categoryId ?? undefined,
    query.search ?? undefined,
    query.page ?? undefined,
    query.pageSize ?? undefined,
  ] as const;

  const response =
    kind === 'article'
      ? await articleClient().getArticlesPublic(...args)
      : await newsItemClient().getNewsItemsPublic(...args);

  const data = response?.data;
  // nswag types `results` with an unrelated DTO's array type; the runtime shape is this one.
  const results = (data?.results ?? []) as unknown as ContentItemPublicDTO[];
  return {
    items: results.map((dto) => toHubItem(kind, dto)),
    total: data?.totalCount ?? 0,
  };
};

/**
 * Resolves a single active article or news item (with its HTML body) by slug or GUID.
 *
 * One round trip: the backend reads the body out of blob storage itself, exactly as
 * `fetchPageBySlug` does — the browser never touches storage, which would fail CORS.
 *
 * Returns null when nothing matches. The backend answers 404 for a missing *or inactive*
 * item, which is an expected outcome a reader should see a friendly message for, not a
 * failure. Any other error throws.
 */
export const fetchHubItem = async (
  kind: HubItemKind,
  idOrSlug: string,
): Promise<HubItemDetail | null> => {
  try {
    const response =
      kind === 'article'
        ? await articleClient().getArticlePublicByIdOrSlug(idOrSlug)
        : await newsItemClient().getNewsItemPublicByIdOrSlug(idOrSlug);

    const dto = response?.data;
    if (!dto) {
      return null;
    }
    return { ...toHubItem(kind, dto), contentHtml: dto.content ?? '' };
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
};

/**
 * Fetches the rendered HTML of an article or news item.
 *
 * The body is stored out-of-row as a blob, and the browser must never fetch a blob SAS URL
 * itself — that fails CORS. So the backend reads it and hands back the HTML, the same
 * arrangement `fetchPageBySlug` relies on. Resolves to an empty string when the record has
 * no content file yet; returns null when the record no longer exists.
 */
export const fetchHubItemHtml = async (
  kind: HubItemKind,
  id: string,
): Promise<string | null> => {
  try {
    const response =
      kind === 'article'
        ? await articleClient().getArticleContent(id)
        : await newsItemClient().getNewsItemContent(id);
    return response?.data ?? '';
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
};

/**
 * Resolves a FileRecord id to a readable URL (a short-lived SAS link).
 *
 * Returns an empty string while the file is still being virus-scanned, or when it can't be
 * resolved at all — a missing picture is never worth failing a render over, so this swallows
 * the error and lets the caller fall back to a placeholder.
 */
export const fetchFileUrl = async (fileRecordId: string): Promise<string> => {
  if (!fileRecordId) {
    return '';
  }
  try {
    const response = await fileClient().getFileUrl(fileRecordId);
    const file = response?.data;
    return file?.status === 'Processed' ? (file.url ?? '') : '';
  } catch {
    return '';
  }
};
